const pad = (value: number, size = 2) => String(Math.floor(value)).padStart(size, '0');

/** `01:23:45.678` — the format a capture deck would print. */
export function formatTimecode(microseconds: number, withMillis = true): string {
  const total = Math.max(0, microseconds);
  const hours = Math.floor(total / 3_600_000_000);
  const minutes = Math.floor((total % 3_600_000_000) / 60_000_000);
  const seconds = Math.floor((total % 60_000_000) / 1_000_000);
  const millis = Math.floor((total % 1_000_000) / 1000);
  const base = `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  return withMillis ? `${base}.${pad(millis, 3)}` : base;
}

/** `12:04` — compact duration for chips and stats. */
export function formatClock(microseconds: number): string {
  const total = Math.max(0, microseconds);
  const hours = Math.floor(total / 3_600_000_000);
  const minutes = Math.floor((total % 3_600_000_000) / 60_000_000);
  const seconds = Math.floor((total % 60_000_000) / 1_000_000);
  return hours > 0
    ? `${hours}:${pad(minutes)}:${pad(seconds)}`
    : `${pad(minutes)}:${pad(seconds)}`;
}

/**
 * Ruler label that stays unique for the step it labels: sub-second ticks need a
 * tenths digit, otherwise five ticks in a row all read `00:02`.
 */
export function formatTick(microseconds: number, stepUs: number): string {
  const total = Math.max(0, microseconds);
  const hours = Math.floor(total / 3_600_000_000);
  const minutes = Math.floor((total % 3_600_000_000) / 60_000_000);
  const seconds = (total % 60_000_000) / 1_000_000;
  const whole = Math.floor(seconds);
  const body = stepUs < 1_000_000 ? `${pad(whole)}.${Math.floor((seconds - whole) * 10)}` : pad(whole);
  return hours > 0 ? `${pad(hours)}:${pad(minutes)}:${body}` : `${pad(minutes)}:${body}`;
}

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 MB';
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const digits = value >= 100 || unit === 0 ? 0 : 1;
  return `${value.toFixed(digits)} ${units[unit]}`;
}

export function formatBitrate(bitsPerSecond: number): string {
  if (!Number.isFinite(bitsPerSecond) || bitsPerSecond <= 0) return '—';
  const mbps = bitsPerSecond / 1_000_000;
  return mbps >= 10 ? `${mbps.toFixed(0)} Mbps` : `${mbps.toFixed(1)} Mbps`;
}

export function buildFilename(
  container: 'mp4' | 'mov',
  width: number,
  height: number,
  fps: number,
  date = new Date(),
): string {
  const stamp = [
    date.getFullYear(),
    pad(date.getMonth() + 1),
    pad(date.getDate()),
  ].join('');
  const time = [pad(date.getHours()), pad(date.getMinutes()), pad(date.getSeconds())].join('');
  const shortSide = Math.min(width, height);
  return `opaleye-${stamp}-${time}-${shortSide}p${fps}.${container}`;
}
